import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { t } from 'utilities/TranslationUtils';
import { DefaultRoutes } from 'routePaths';
import { countTitle } from 'pages/MeasurementDetail/kickWording';
import { MeasurementMarkerDto, SessionOptionDto, formatKindLabel, SessionKindGroupName } from 'types/models/SessionsPageData';
import { VideoLite } from 'stores/PlayerPerformanceStore';
import { buildKickVideos } from 'utilities/videoUrls';
import { cornerOnTargetPct } from 'utilities/kickUtils';
import KickResultChip from './KickResultChip';

const MODAL_WIDTH = 380;
const MODAL_HEIGHT_ESTIMATE = 280;

type Props = {
  measurement: MeasurementMarkerDto;
  sessions: SessionOptionDto[];
  position: { x: number; y: number };
  isSelected: boolean;
  allowSessionDetailsFetch?: boolean;
  onToggleSelect: () => void;
  onClose: () => void;
  simplifiedView?: boolean;
  onShowInTable?: (m: MeasurementMarkerDto) => void;
  /** Narrows the page to this kick's session (simplified view). */
  onFilterSession?: (m: MeasurementMarkerDto) => void;
  /** How many kicks / throws of this player the kick's session holds, for the "Show session" button (simplified view). */
  sessionKickCount?: number;
  /** Fired when user hovers over the modal (prevents closure). */
  onMouseEnter?: () => void;
  /** Fired when user leaves the modal. */
  onMouseLeave?: () => void;
};

/**
 * Round 17: this is now the SOLE info surface — replaces the prior dark hover-tooltip. The card
 * is rendered both on hover and on click; clicking the Select/Deselect button toggles selection
 * but leaves the modal open so the user can toggle back without re-hovering.
 *
 * Lazily fetches video info from /api/SessionDetails — counts videos with non-empty URLs and
 * keeps the full URL list around so the video chip can open a small player overlay.
 */
const KickInfoModal = observer(({ measurement, sessions, position, isSelected, allowSessionDetailsFetch = false, onToggleSelect, onClose, simplifiedView = false, onShowInTable, onFilterSession, sessionKickCount, onMouseEnter, onMouseLeave }: Props): React.ReactNode => {
  const { apiServiceAxiosInstance: api, playerPerformanceStore: store, sasTokenStore } = useStores();
  const ref = useRef<HTMLDivElement>(null);
  const [videoState, setVideoState] = useState<'idle' | 'loading' | 'empty' | 'error'>('idle');
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playerIndex, setPlayerIndex] = useState(0);

  const fetchedVideos = store.sessionDetailsFor(measurement.sessionId, measurement.id)?.videos ?? null;

  // Preferred path: build blob video URLs directly from the marker's videoIds + the preloaded SAS token,
  // so opening a kick's videos needs no /api/SessionDetails round-trip. Falls back to the fetched videos.
  const builtVideos = useMemo(
    () => buildKickVideos(sasTokenStore.token, measurement.sessionId, measurement.id, measurement.videoIds),
    [sasTokenStore.token, measurement.sessionId, measurement.id, measurement.videoIds],
  );
  const videos = builtVideos.length > 0 ? builtVideos : fetchedVideos;

  const left = clampLeft(position.x);
  const top = clampTop(position.y);

  // Reset UI state when the user hovers a different kick.
  useEffect(() => {
    setVideoState('idle');
    setPlayerOpen(false);
    setPlayerIndex(0);
  }, [measurement.id, measurement.sessionId]);

  const fetchAndShowVideos = async () => {
    // Built directly from videoIds + SAS — no session fetch needed.
    if (builtVideos.length > 0) { setPlayerIndex(0); setPlayerOpen(true); return; }
    if (!allowSessionDetailsFetch) return;
    if (videos && videos.length > 0) { setPlayerOpen(true); return; }
    setVideoState('loading');
    try {
      await store.loadSessionDetails(api, measurement.sessionId, measurement.id);
      const loaded = store.sessionDetailsFor(measurement.sessionId, measurement.id)?.videos ?? [];
      if (loaded.length === 0) setVideoState('empty');
      else { setVideoState('idle'); setPlayerIndex(0); setPlayerOpen(true); }
    } catch {
      setVideoState('error');
    }
  };

  // Click-outside / Esc to close — but only when the video sub-modal isn't open (its own backdrop catches outside-clicks).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (playerOpen) return;
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (playerOpen) { setPlayerOpen(false); return; }
      onClose();
    };
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, playerOpen]);

  const session = sessions.find((s) => s.id === measurement.sessionId);
  const sessionDate = session ? new Date(session.capturedAtUtc) : null;
  const kickTime = new Date(measurement.capturedAtUtc);
  const kindLabel = formatKindLabel(measurement.kind as SessionKindGroupName);
  const cornerPct = cornerOnTargetPct(measurement.kind, measurement.onTarget);

  const detailUrl = `${DefaultRoutes.MeasurementDetailPage}?sessionId=${encodeURIComponent(measurement.sessionId)}&measurementId=${encodeURIComponent(measurement.id)}`;
  const openMoreInfo = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const win = window.open(detailUrl, '_blank', 'noopener,noreferrer');
    if (win) win.opener = null;
  };

  // Round 19: no automatic chip — the meta row stays compact (kind + result) and a dedicated
  // "Show videos" button sits in the actions area. The button label reflects state:
  //   idle:    "Show videos"
  //   loading: "Loading videos…" (disabled)
  //   empty:   "No videos" (disabled)
  //   error:   "Couldn't load videos" (clickable to retry)
  const knownVideoCount = measurement.videoCount;
  const videoCountLabel = (count: number): string => (
    count === 1
      ? t('sessions.kickModalVideosOne')
      : t('sessions.kickModalVideosMany', { count })
  );
  const showVideosLabel =
    !allowSessionDetailsFetch && knownVideoCount !== null && knownVideoCount !== undefined ? videoCountLabel(knownVideoCount) :
    !allowSessionDetailsFetch ? t('sessions.kickModalShowVideos') :
    videoState === 'empty'   ? t('sessions.kickModalNoVideos') :
    videoState === 'error'   ? t('sessions.kickModalVideosRetry') :
    videos && videos.length > 0 ? videoCountLabel(videos.length) :
    knownVideoCount !== null && knownVideoCount !== undefined ? videoCountLabel(knownVideoCount) :
    t('sessions.kickModalShowVideos');
  const showVideosDisabled = (!allowSessionDetailsFetch && builtVideos.length === 0) || videoState === 'loading' || videoState === 'empty';

  return (
    <>
      <div
        ref={ref}
        className='kick-info-modal'
        style={{ position: 'fixed', left, top, width: MODAL_WIDTH, zIndex: 1200 }}
        role='dialog'
        aria-label={t('sessions.kickModalAriaLabel')}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className='kick-info-modal-header'>
          <span className='kick-info-modal-player' title={measurement.playerName}>{measurement.playerName}</span>
          <button type='button' className='kick-info-modal-close' onClick={onClose} aria-label={t('sessions.kickModalClose')}>×</button>
        </div>
        <div className='kick-info-modal-meta'>
          <span className='kick-info-modal-chip'>{kindLabel}</span>
          {measurement.result && <KickResultChip result={measurement.result} kind={measurement.kind} onTarget={measurement.onTarget ?? null} />}
        </div>
        <dl className='kick-info-modal-grid'>
          {measurement.ballSpeed !== null && (
            <><dt>{t('measurementDetail.ballSpeed')}</dt><dd>{measurement.ballSpeed} {t('common.unitKmh')}</dd></>
          )}
          {measurement.hangTime !== null && (
            <><dt>{t('measurementDetail.hangTime')}</dt><dd>{measurement.hangTime}{t('common.unitSeconds')}</dd></>
          )}
          {cornerPct !== null && (
            <><dt>{t('sessions.kickModalOnTarget')}</dt><dd>{cornerPct}%</dd></>
          )}
          <dt>{t('sessions.tooltipKickTime')}</dt>
          <dd>{kickTime.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</dd>
          {sessionDate && (
            <><dt>{t('sessions.tooltipSessionDate')}</dt><dd>{sessionDate.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</dd></>
          )}
        </dl>
        <div className='kick-info-modal-ids'>
          <CopyButton text={measurement.id}    label={t('sessions.kickModalCopyKickId')} />
          <CopyButton text={measurement.sessionId} label={t('sessions.kickModalCopySessionId')} />
        </div>

        {simplifiedView ? (
          <div className='kick-info-modal-actions'>
            {onShowInTable && (
              <button
                type='button'
                className='kick-info-modal-primary'
                onClick={() => { onShowInTable(measurement); onClose(); }}
              >
                {t('sessions.kickModalShowInTable')}
              </button>
            )}
            {onFilterSession && (
              <button
                type='button'
                className='kick-info-modal-secondary'
                onClick={() => { onFilterSession(measurement); onClose(); }}
                data-test='kick-info-filter-session'
              >
                {t('sessions.kickModalFilterSession')}
              </button>
            )}
            <a
              href={detailUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='kick-info-modal-session'
              onClick={openMoreInfo}
              data-test='kick-info-show-session'
            >
              {sessionKickCount != null
                ? t('playerPerformance.showSession', { title: countTitle(measurement.kind === 'ThrowIn', sessionKickCount) })
                : t('sessions.kickModalShowSession')}
            </a>
          </div>
        ) : (
          <div className='kick-info-modal-actions'>
            <button
              type='button'
              className={`kick-info-modal-primary ${isSelected ? 'kick-info-modal-primary--remove' : ''}`}
              onClick={onToggleSelect}
            >
              {isSelected ? t('sessions.kickModalRemove') : t('sessions.kickModalAdd')}
            </button>
            <button
              type='button'
              className='kick-info-modal-secondary'
              onClick={fetchAndShowVideos}
              disabled={showVideosDisabled}
              style={{ minWidth: '120px', position: 'relative' }}
            >
              {videoState === 'loading' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span className='sessions-spinner' style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                </span>
              ) : showVideosLabel}
            </button>
            <a
              href={detailUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='kick-info-modal-secondary'
              onClick={openMoreInfo}
            >
              {t('sessions.kickModalMoreInfo')} →
            </a>
          </div>
        )}

        {playerOpen && Array.isArray(videos) && videos.length > 0 && (
          <VideoPlayerOverlay
            videos={videos}
            index={playerIndex}
            onIndexChange={setPlayerIndex}
            onClose={() => setPlayerOpen(false)}
            measurement={measurement}
          />
        )}
      </div>

    </>
  );
});

/** Small dashed-pill copy button — flips its label to "Copied" for 1.5s after a successful copy. */
const CopyButton = ({ text, label }: { text: string; label: string }): React.ReactElement => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);
  const onClick = async () => {
    try { await navigator.clipboard.writeText(text); } catch { return; }
    setCopied(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type='button'
      className={`kick-info-modal-copy${copied ? ' kick-info-modal-copy--copied' : ''}`}
      onClick={onClick}
      title={text}
    >
      {copied ? t('sessions.kickModalCopied') : label}
    </button>
  );
};

const VideoPlayerOverlay = ({
  videos, index, onIndexChange, onClose, measurement,
}: {
  videos: VideoLite[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  measurement: MeasurementMarkerDto;
}): React.ReactElement => {
  const current = videos[Math.max(0, Math.min(index, videos.length - 1))];
  const videoCount = videos.length;
  return (
    <div className='kick-info-video-backdrop' onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className='kick-info-video-modal'>
        <div className='kick-info-video-header'>
          <span>{current?.label ?? t('sessions.kickModalVideosOne')}</span>
          <button type='button' className='kick-info-modal-close' onClick={onClose} aria-label={t('sessions.kickModalClose')}>×</button>
        </div>
        {current?.url ? (
          <video
            className='kick-info-video-element'
            src={current.url}
            controls
            autoPlay
            playsInline
            poster={current.thumbnailUrl}
          />
        ) : (
          <p className='kick-info-video-empty'>{t('sessions.kickModalVideoUnavailable')}</p>
        )}
        {current?.url && (measurement.ballSpeed !== null || (measurement.distance !== null && measurement.distance !== undefined)) && (
          <div className='kick-info-metrics'>
            {measurement.ballSpeed !== null && (
              <div className='kick-info-metric'>
                <div className='kick-info-metric-label'>{t('measurementDetail.ballSpeed')}</div>
                <div className='kick-info-metric-value'>{measurement.ballSpeed.toFixed(1)} {t('common.unitKmh')}</div>
              </div>
            )}
            {measurement.distance !== null && measurement.distance !== undefined && (
              <div className='kick-info-metric'>
                <div className='kick-info-metric-label'>{t('measurementDetail.distance')}</div>
                <div className='kick-info-metric-value'>{measurement.distance.toFixed(1)} {t('common.unitMeters')}</div>
              </div>
            )}
          </div>
        )}
        {videoCount > 1 && (
          <div className='kick-info-video-tabs'>
            {videos.map((v, i) => (
              <button
                key={v.videoId ?? i}
                type='button'
                className={`kick-info-video-tab${i === index ? ' kick-info-video-tab--active' : ''}`}
                onClick={() => onIndexChange(i)}
              >
                {v.label ?? `${t('sessions.kickDetailCamera')} ${i + 1}`}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function clampLeft(x: number): number {
  const maxX = window.innerWidth - MODAL_WIDTH - 16;
  return Math.max(16, Math.min(maxX, x + 12));
}

function clampTop(y: number): number {
  const maxY = window.innerHeight - MODAL_HEIGHT_ESTIMATE - 16;
  return Math.max(16, Math.min(maxY, y + 12));
}

export default KickInfoModal;
